import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "components/ui/Select";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import DPATAssessment from "pages/DPATAssessment";
import { useLocationContext } from "context/LocationContext";

const YEAR_OPTIONS = Array.from({ length: 12 }, (_, index) => {
  const y = 2015 + index;
  return { label: y, value: y };
});

const WIZARD_STEPS = [
  {
    key: "context",
    title: "Confirm district context",
    body: "DPAT pulls Data from Trackers and CMS indicators for the district shown in the sidebar. Confirm if this is the assembly you intend to assess before loading the live sheet.",
  },
  {
    key: "year",
    title: "Choose reporting year",
    body: "Indicators and tracker windows are filtered by the selected calendar year. Pick the year that matches the assessment cycle you are working on.",
  },
  {
    key: "live",
    title: "Open the live score sheet",
    body: "The full annex workbook loads in the pane below. Click on the Start Assessment button to begin the assessment process and Use  print / PDF from the sheet when you need a softcopy; pagination controls live inside the sheet header.",
  },
];

function toYearNumber(value) {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "object" && value.value != null) return Number(value.value);
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function DistrictRequiredState() {
  return (
    <div className="main-content">
      <div className="card">
        <h2 className="text-xl font-semibold text-foreground mb-2">District Selection Required</h2>
        <p className="text-muted-foreground">
          DPAT Assessment is available at district level. Select a district from the sidebar, then open this module.
        </p>
      </div>
    </div>
  );
}

function DistrictIdUnavailableState({ districtName }) {
  return (
    <div className="main-content">
      <div className="card">
        <h2 className="text-xl font-semibold text-foreground mb-2">District ID Not Available</h2>
        <p className="text-muted-foreground">
          {districtName || "This district"} is visible in the static MMDA hierarchy, but its DHIS2 org unit ID has not been loaded yet.
          Refresh the org unit cache or reconnect to DHIS2, then try DPAT Assessment again.
        </p>
      </div>
    </div>
  );
}

export default function DPATModuleAdapter() {
  const { currentLocation } = useLocationContext();
  const [searchParams] = useSearchParams();
  const currentCalendarYear = new Date().getFullYear();
  const yearFromQuery = Number(searchParams.get("year"));
  const defaultYearOption =
    (Number.isFinite(yearFromQuery) && YEAR_OPTIONS.find((option) => option.value === yearFromQuery)) ||
    YEAR_OPTIONS.find((option) => option.value === currentCalendarYear) ||
    YEAR_OPTIONS[YEAR_OPTIONS.length - 1];

  const [wizardStepIndex, setWizardStepIndex] = useState(0);
  const [phase, setPhase] = useState(() => (Number.isFinite(yearFromQuery) ? "live" : "wizard"));
  const [selectedYear, setSelectedYear] = useState(() => defaultYearOption?.value ?? 2024);
  const sheetAnchorRef = useRef(null);

  useEffect(() => {
    if (!Number.isFinite(yearFromQuery)) return;
    const matched = YEAR_OPTIONS.find((option) => option.value === yearFromQuery);
    if (matched) {
      setSelectedYear(matched.value);
      setPhase("live");
      setWizardStepIndex(WIZARD_STEPS.length - 1);
    }
  }, [yearFromQuery]);

  const selectedYearOption = useMemo(
    () => YEAR_OPTIONS.find((o) => o.value === selectedYear) ?? defaultYearOption ?? YEAR_OPTIONS[0],
    [selectedYear, defaultYearOption],
  );

  const selectedDistrictOption = useMemo(() => {
    if (currentLocation?.level !== "district") return null;
    const orgUnitId = currentLocation.orgUnitId || null;
    if (!orgUnitId) return null;

    return {
      label: currentLocation.name,
      value: {
        id: orgUnitId,
        region: currentLocation.regionName,
      },
    };
  }, [currentLocation]);

  const yearNumber = useMemo(() => toYearNumber(selectedYear), [selectedYear]);

  useEffect(() => {
    if (phase !== "live") return undefined;
    const id = window.requestAnimationFrame(() => {
      sheetAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [phase]);

  const handleYearSelectChange = useCallback((option) => {
    const next = option?.value ?? selectedYear;
    setSelectedYear(typeof next === "number" ? next : toYearNumber(next) ?? selectedYear);
  }, [selectedYear]);

  const goLive = useCallback(() => {
    setPhase("live");
    setWizardStepIndex(WIZARD_STEPS.length - 1);
  }, []);

  if (currentLocation?.level !== "district") {
    return <DistrictRequiredState />;
  }

  if (!selectedDistrictOption) {
    return <DistrictIdUnavailableState districtName={currentLocation?.name} />;
  }

  return (
    <div className="min-h-full bg-background">
      <div className="main-content pb-0">
        <div className="card p-4 md:p-5 space-y-4">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">DPAT Assessment Module</p>
              <h2 className="text-lg font-semibold text-foreground">{currentLocation.regionName}</h2>
              <p className="text-sm text-muted-foreground"><strong>{currentLocation.name}</strong> assessment template</p>
             
            </div>
            <div className="w-full md:w-56 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Assessment year</p>
              <Select
                options={YEAR_OPTIONS}
                value={selectedYearOption}
                onChange={handleYearSelectChange}
                placeholder="Select year"
              />
            </div>
          </div>


          {phase === "wizard" && (
            <div className="flex flex-wrap gap-3 items-center">
              <Button type="button" variant="default" size="lg" onClick={goLive} className="min-h-[48px] px-8 text-base">
                Click to view {selectedYear ?? "selected year"} assessment sheet
              </Button>
            </div>
          )}

          {phase === "live" && (
            <div className="sticky top-0 z-10 -mx-4 md:-mx-5 px-4 md:px-5 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Live sheet</p>
                <p className="text-sm font-semibold text-foreground">
                  {currentLocation.name} · {yearNumber ?? "—"}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setPhase("wizard")}>
                  Back to briefing
                </Button>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => sheetAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                >
                  Scroll to sheet
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div ref={sheetAnchorRef} className="main-content pt-4">
        {phase === "live" && (
          <DPATAssessment selectedLocation={selectedDistrictOption} timePeriod={yearNumber ?? selectedYear} />
        )}
      </div>
    </div>
  );
}
